//Built in Libraries
import { Fragment, useState } from "react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";

//Third Part Libraries
import { Transition, Menu } from "@headlessui/react";
import {
  AdjustmentsIcon,
  LibraryIcon,
  LogoutIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
} from "@heroicons/react/solid";

//Components
import { authSelector } from "../../../store";
import NewUserForm from "../../Users/NewUserForm";
import Modal from "../../UI/Modal";
import NewTitleForm from "../../Administration/NewTitleForm";
import { getInitials } from "../../../utils";
import ImageCircularAvatar from "../../UI/Avatar/ImageCircularAvatar";
import SimpleDropDown, { DropDownItem } from "../../UI/SimpleDropDown";
import PlusIcon from "../../UI/Icons/PlusIcon";
import Notifications from "./Notifications";
import { PermissionCommons } from "../../../types/Permission";
import { HasPermission } from "../../Permission/HasPermission";
import { usePermission } from "../../../hooks/usePermission";
import { NewCategoryForm } from "features/Category";
import { useCurrentAccountData } from "../../../hooks/ReactQueryHooks/useCurrentAccountQuery";
import { Link } from "react-router-dom";
import NewVendorForm from "../../Vendor/NewVendorForm";
import { Workspace } from "../../Workspace";
import useEnvironment from "../../../hooks/useEnvironment";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

interface GlobalAddOption {
  name: string;
  modalName: string;
  permission?: PermissionCommons;
}
const globalAddOptions: GlobalAddOption[] = [
  {
    name: "Create New Project",
    modalName: "createNewProject",
    permission: { of: "CREATE_PROJECT" },
  },
  {
    name: "Add New Title",
    modalName: "addNewTitle",
    permission: { of: "CREATE_TITLE" },
  },
  {
    name: "Invite New User",
    modalName: "inviteNewUser",
    permission: { of: "INVITE_USER" },
  },
  {
    name: "Invite New Vendor",
    modalName: "inviteNewVendor",
  },
  {
    name: "Add New Category",
    modalName: "addNewCategory",
  },
];

export default function HeaderBar() {
  const currentAccountInfo = useCurrentAccountData({});
  const accountSubdomain = currentAccountInfo?.data?.data?.subdomain;

  const navigate = useNavigate();
  const { data: currentUserData } = useSelector(authSelector);
  const { hasPermission } = usePermission();
  const [isModalOpen, setIsModalOpen] = useState<{ [key: string]: boolean }>({
    createNewProject: false,
    inviteNewUser: false,
    inviteNewVendor: false,
    addNewTitle: false,
    addNewCategory: false,
  });
  const currentEnv = useEnvironment();

  const modalHandler = (modalName: string) => {
    if (modalName === "createNewProject") {
      //New project form is pretty big to display on the modal.
      //so, pushing it to another page
      return navigate("/app/sherpa/projects/new");
    }

    //Handling all Modals needed in this component
    setIsModalOpen((prevState) => {
      return { ...prevState, [modalName]: !prevState[modalName] };
    });
  };

  const filterGlobalAddOptions = globalAddOptions.filter((item) => {
    if (
      currentUserData?.account === "sageadmin" &&
      (item.name === "Create New Project" || item.name === "Add New Category")
    ) {
      return false;
    }
    return item;
  });

  return (
    <>
      <header className="w-full">
        <div className="relative z-20 flex-shrink-0 h-16 bg-white border-b border-gray-200 shadow-sm flex">
          <div className="flex-1 flex justify-between px-4 sm:px-6 w-full">
            <div className="flex">
              {/* <form className="w-full flex md:ml-0" action="#" method="GET">
                <label htmlFor="search-field" className="sr-only">
                  Search all files
                </label>
                <div className="relative w-full text-gray-400 focus-within:text-gray-600">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center">
                    <SearchIcon
                      className="flex-shrink-0 h-5 w-5"
                      aria-hidden="true"
                    />
                  </div>
                  <input
                    name="search-field"
                    id="search-field"
                    className="h-full w-full outline-none border-0 py-2 pl-8 pr-3 text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-transparent focus:placeholder-gray-400"
                    placeholder="Search"
                    type="search"
                  />
                </div>
              </form> */}
            </div>
            <div className="ml-2 flex justify-end items-center space-x-4 sm:ml-6 sm:space-x-4 relative max-w-screen-sm w-full md:w-2/3 xl:w-2/5">
              <Workspace />
              <Notifications />
              {(accountSubdomain === "chicagorebuild" || currentEnv.isDev) && (
                <span
                  className={`text-neutralColor-normal hover:text-primaryColor-normal cursor-pointer`}
                >
                  <Link to="/app/learn">
                    <QuestionMarkCircleIcon className="h-8 w-8" />
                  </Link>
                </span>
              )}
              {isModalOpen.inviteNewUser && (
                <Modal>
                  <NewUserForm formModalHandler={modalHandler} />
                </Modal>
              )}
              {isModalOpen.addNewTitle && (
                <Modal>
                  <NewTitleForm closeForm={() => modalHandler("addNewTitle")} />
                </Modal>
              )}
              {isModalOpen.addNewCategory && (
                <NewCategoryForm
                  handleModal={() => modalHandler("addNewCategory")}
                />
              )}
              {isModalOpen.inviteNewVendor && (
                <Modal>
                  <NewVendorForm
                    handleModal={() => modalHandler("inviteNewVendor")}
                  />
                </Modal>
              )}
              {hasPermission([
                "CREATE_PROJECT",
                "CREATE_TITLE",
                "INVITE_USER",
                "CREATE_CATEGORY",
              ]) && (
                <Menu as="div" className="relative flex-shrink-0 mr-4">
                  <div>
                    <Menu.Button className="bg-sky-500 rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-sky-500 focus:ring-white">
                      <span className="sr-only">Open user menu</span>
                      <PlusIcon />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {filterGlobalAddOptions.map((item) => {
                        const menuItem = (
                          <Menu.Item key={item.name}>
                            {({ active }) => {
                              return (
                                <span
                                  className={classNames(
                                    active
                                      ? "bg-green-50 border-l-4 border-green-500 border-opacity-100"
                                      : "",
                                    "block px-4 py-2 text-sm text-gray-700 cursor-pointer border-l-4 border-opacity-0"
                                  )}
                                  onClick={() => modalHandler(item.modalName)}
                                >
                                  {item.name}
                                </span>
                              );
                            }}
                          </Menu.Item>
                        );
                        const { permission } = item;
                        if (permission) {
                          return (
                            <HasPermission key={item.name} of={permission.of}>
                              {menuItem}
                            </HasPermission>
                          );
                        }
                        return <div key={item.name}>{menuItem}</div>;
                      })}
                    </Menu.Items>
                  </Transition>
                </Menu>
              )}
              <Menu as="div" className="relative flex-shrink-0">
                <div className="flex items-center gap-4">
                  {/* <div
                    className="flex flex-col cursor-pointer"
                    onClick={() => navigate("/app/settings/profile")}
                  >
                    <h3 className='text-sm font-bold'>
                      {currentUserData.name
                        ? currentUserData.name
                        : "unknown user"}
                    </h3>
                    <p className='text-xs text-neutralColor-normal'>
                      {currentUserData.title}
                    </p>
                  </div> */}
                  <SimpleDropDown
                    menuButtonClassName="bg-white rounded-md flex items-center text-sm"
                    itemContainerWidth="w-72"
                    toggle={
                      <>
                        <span className="sr-only">User profile</span>
                        <ImageCircularAvatar
                          imageUrl={currentUserData.profileImageUrl}
                          fallbackText={getInitials(currentUserData.name)}
                        />
                      </>
                    }
                  >
                    <DropDownItem id="name" dropDownTitle>
                      <div className="truncate">
                        <h3 className="text-sm font-bold truncate">
                          {currentUserData.name
                            ? currentUserData.name
                            : "unknown user"}
                        </h3>
                        <p className="text-xs text-neutralColor-normal truncate">
                          {currentUserData.title}
                        </p>
                      </div>
                    </DropDownItem>
                    <DropDownItem
                      id="profile"
                      onClick={() => navigate("/app/settings/profile")}
                    >
                      <div className="flex gap-2">
                        <UserCircleIcon className="h-5 w-5" />
                        <span>Profile</span>
                      </div>
                    </DropDownItem>
                    <DropDownItem
                      id="profile"
                      onClick={() => navigate("/app/settings/notifications")}
                    >
                      <div className="flex gap-2">
                        <AdjustmentsIcon className="h-5 w-5" />
                        <span>Notification Preference</span>
                      </div>
                    </DropDownItem>
                    {hasPermission(["READ_COMPANY_PROFILE"]) && (
                      <DropDownItem
                        id="profile"
                        onClick={() =>
                          navigate("/app/settings/company-profile")
                        }
                      >
                        <div className="flex gap-2">
                          <LibraryIcon className="h-5 w-5" />
                          <span>Company Profile</span>
                        </div>
                      </DropDownItem>
                    )}
                    <DropDownItem
                      id="logout"
                      onClick={() => navigate("/logout")}
                    >
                      <div className="flex gap-2">
                        <LogoutIcon className="h-5 w-5" />
                        <span>Logout</span>
                      </div>
                    </DropDownItem>
                  </SimpleDropDown>
                </div>
              </Menu>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
